import { Injectable } from "@angular/core";
import { CanMatch, Route, UrlSegment } from "@angular/router";
import { MobileService } from "../services/mobile.service";
import { UserService } from "./user.service";

/**
 * FLIP-528. `canMatch` guard for the public landing route.
 *
 * Matches (so the landing page renders) only for a browser visitor: not the native app shell and no
 * valid session. Otherwise it returns false, and the router falls through to the next route that
 * matches the same URL - the existing authenticated routes - so the installed app, logged-in users
 * and deep links behave exactly as before.
 *
 * The native shell announces itself with `?appPlatform=` on its first load. Like MobileOnlyGuard,
 * this reads that param itself rather than trusting MobileService to have seen it already; the
 * platform is then persisted in localStorage by MobileService for later loads.
 */
@Injectable()
export class PublicLandingGuard implements CanMatch {
  constructor(
    private userService: UserService,
    private mobileService: MobileService,
  ) {}

  /** The router isn't navigated yet when canMatch runs, so read the query string off the location. */
  protected locationSearch(): string {
    return window.location.search;
  }

  canMatch(_route: Route, _segments: UrlSegment[]): boolean {
    const appPlatform = new URLSearchParams(this.locationSearch()).get("appPlatform");
    if (appPlatform) {
      this.mobileService.setPlatformType(appPlatform);
    }
    // MobileService.init() may not have run yet; getPlatformType() reads the persisted value directly.
    const platform = this.mobileService.getPlatformType();
    const isShell = this.mobileService.isMobileApp || platform === this.mobileService.ANDROID || platform === this.mobileService.IOS;
    if (isShell) {
      return false;
    }

    const hasSession = !!this.userService.getUser() && !this.userService.isTokenExpired();
    return !hasSession;
  }
}
